"use client";
import React, { useState } from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { Box, Modal, IconButton, Fade, Grid } from "@mui/material";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-fade";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const solutionsData = [
  {
    itemKey: "item1",
    images: [
      "/assets/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl/solutions/img1-1.png",
      "/assets/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl/solutions/img1-2.png",
    ],
  },
  {
    itemKey: "item2",
    images: [
      "/assets/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl/solutions/img2-1.png",
      "/assets/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl/solutions/img2-2.png",
    ],
  },
  {
    itemKey: "item3",
    images: [
      "/assets/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl/solutions/img3-1.png",
      "/assets/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl/solutions/img3-2.png",
    ],
  },
  {
    itemKey: "item4",
    images: [
      "/assets/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl/solutions/img4-1.png",
      "/assets/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl/solutions/img4-2.png",
    ],
  },
];

export default function ExpertLoadingSolutions() {
  const t = useTranslations("OurServices");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const handleOpenLightbox = (images: string[], index: number) => {
    setSelectedImages(images);
    setSelectedImageIndex(index);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => setLightboxOpen(false);

  const handlePrevImage = () => {
    setSelectedImageIndex((prevIndex) =>
      prevIndex === 0 ? selectedImages.length - 1 : prevIndex - 1
    );
  };

  const handleNextImage = () => {
    setSelectedImageIndex((prevIndex) =>
      prevIndex === selectedImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  return (
    <Box
      className="our-services-solutions"
      sx={{
        pt: { xs: 5, lg: 5 },
        pb: { xs: 0, lg: 5 }
      }}>
      <Container
         className="container"
         sx={{
           display: "flex",
           flexDirection: "column",
           alignItems: { xs: "center", lg: "flex-end" },
         }}
       >
         <Typography
           variant="h2"
           data-aos="fade-up"
           sx={{
             mb: 5,

             //
             whiteSpace: { xs: "normal", lg: "pre-line" },

             textAlign: { xs: "center", lg: "left" }
           }}>
           {t("Solutions.title")}
         </Typography>
         <Grid
           container
           spacing={3}
           sx={{
             justifyContent: { xs: "center", lg: "flex-end" },
             width: "100%",
           }}
         >
           <Grid size={{ xs: 12, lg: 8 }}>
             <Grid
               container
               spacing={3}
               data-aos="zoom-in"
               className="isolutions-wrap"
             >
               {solutionsData.map((solution, solutionIndex) => {
                 let size = 6; // default size
                 if (solutionIndex === 2) {
                   size = 5;
                 } else if (solutionIndex === 3) {
                   size = 7;
                 }
                 return (
                   <Grid
                     size={size}
                     key={solutionIndex}
                     className="isolutions-accordion"
                   >
                     <Swiper
                       slidesPerView={1}
                       rewind={true}
                       effect={"fade"}
                       autoplay={{
                         delay: 5000,
                       }}
                       modules={[Autoplay, EffectFade]}
                     >
                       {solution.images.map((imageSrc, imageIndex) => (
                         <SwiperSlide key={imageIndex}>
                           <Box
                             sx={{ cursor: "pointer" }}
                             onClick={() =>
                               handleOpenLightbox(solution.images, imageIndex)
                             }
                           >
                             <Image
                               src={imageSrc}
                               title={"thailand expert loading solutions"}
                               alt={"thailand expert loading solutions"}
                               width={678}
                               height={442}
                               className="img-fluid"
                             />
                             <Box className="isolutions-accordion-content">
                               <Typography
                                 variant="h3"
                                 sx={{
                                   whiteSpace: { xs: "normal", lg: "pre-line" },
                                 }}
                               >
                                 {t(`Solutions.${solution.itemKey}`)}
                               </Typography>
                             </Box>
                           </Box>
                         </SwiperSlide>
                       ))}
                     </Swiper>
                   </Grid>
                 );
               })}
             </Grid>
           </Grid>
         </Grid>
       </Container>
      <Box
        sx={{
          display: { md: "block", lg: "none" },
          mt: 5
        }}>
        <Image
          src="/assets/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl/solutions/bg3.jpg"
          title={"thailand expert loading solutions"}
          alt={"thailand expert loading solutions"}
          width={1477}
          height={1180}
          className="img-fluid"
          style={{ display: "flex" }}
        />
      </Box>
      {/* Lightbox Modal */}
      <Modal
        open={lightboxOpen}
        onClose={handleCloseLightbox}
        closeAfterTransition
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Fade in={lightboxOpen}>
          <Box
            sx={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
              outline: "none",
            }}
          >
            <IconButton
              onClick={handleCloseLightbox}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                color: "white",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.8)" },
                zIndex: 1,
              }}
            >
              <CloseIcon />
            </IconButton>

            {selectedImages.length > 1 && (
              <>
                <IconButton
                  onClick={handlePrevImage}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: 16,
                    transform: "translateY(-50%)",
                    color: "white",
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.8)" },
                  }}
                >
                  <ArrowBackIosNewIcon />
                </IconButton>
                <IconButton
                  onClick={handleNextImage}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    right: 16,
                    transform: "translateY(-50%)",
                    color: "white",
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.8)" },
                  }}
                >
                  <ArrowForwardIosIcon />
                </IconButton>
              </>
            )}
            <img
              src={selectedImages[selectedImageIndex]}
              alt={`Solutions gallery image ${selectedImageIndex + 1}`}
              style={{
                maxWidth: "100%",
                maxHeight: "90vh",
                objectFit: "contain",
              }}
            />
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
}
